import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { statusStyle } from "@/lib/useStatuses";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ImportDialog } from "@/components/ImportDialog";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Tag, Power } from "lucide-react";

const KINDS = [
  { id: "initial", name: "Inicial (nova OS)", help: "Status recebido automaticamente por toda OS nova" },
  { id: "progress", name: "Em atendimento", help: "Aplicado automaticamente quando o atendente abre uma OS inicial" },
  { id: "waiting", name: "Intermediário / aguardando", help: "OS continua em aberto" },
  { id: "resolved", name: "Final — resolvida", help: "Encerra a OS (conta como finalizada)" },
  { id: "cancelled", name: "Final — cancelada", help: "Encerra a OS como cancelada" },
];
const PALETTE = ["#F59E0B", "#3B82F6", "#F97316", "#A855F7", "#6366F1", "#10B981", "#EF4444", "#14B8A6", "#EC4899", "#64748B", "#0EA5E9", "#84CC16"];
const EMPTY = { name: "", color: "#3B82F6", kind: "waiting", active: true };

export default function OsStatuses() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const load = () => api.get("/os-statuses").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Informe o nome do status");
    try {
      if (editing) await api.put(`/os-statuses/${editing.id}`, form); else await api.post("/os-statuses", form);
      toast.success(editing ? "Status atualizado" : "Status criado"); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (s) => {
    if (s.active && !window.confirm(`Inativar o status "${s.name}"? Ele deixa de aparecer para novas OS, mas o histórico é preservado.`)) return;
    try { await api.put(`/os-statuses/${s.id}`, { name: s.name, color: s.color, kind: s.kind, active: !s.active }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (s) => {
    if (!window.confirm(`Excluir o status "${s.name}"?`)) return;
    try { await api.delete(`/os-statuses/${s.id}`); toast.success("Status excluído"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (i, dir) => {
    const ids = rows.map((r) => r.id); const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    const r = await api.post("/os-statuses/reorder", { ids }); setRows(r.data);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
      <PageHeader title="Status da OS" subtitle="Defina os status do seu fluxo de atendimento. Eles aparecem automaticamente na tela de OS, filtros, dashboard e no WhatsApp.">
        <ImportDialog resource="os-statuses" label="status" onDone={load} />
        <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-status-btn"><Plus className="w-4 h-4 mr-2" /> Novo status</Button>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((s, i) => (
          <Card key={s.id} className={`p-5 border-slate-200 dark:border-slate-800 hover:shadow-md transition-shadow ${s.active ? "" : "opacity-60"}`} data-testid={`status-row-${s.name}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${s.color}20` }}><Tag className="w-5 h-5" style={{ color: s.color }} /></div>
                <div className="min-w-0">
                  <h3 className="font-bold text-slate-900 dark:text-white truncate">{s.name}</h3>
                  <p className="text-xs text-slate-500 truncate">{KINDS.find((k) => k.id === s.kind)?.name}</p>
                </div>
              </div>
              <Badge variant="outline" className={`shrink-0 ${s.active ? "text-emerald-600 border-emerald-500/30" : "text-slate-500"}`}>{s.active ? "Ativo" : "Inativo"}</Badge>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Badge variant="outline" className="font-semibold" style={statusStyle(s)}>{s.name}</Badge>
              <span className="text-xs text-slate-500">{s.in_use} OS</span>
              <span className="ml-auto text-[10px] uppercase font-bold text-slate-400">Posição {i + 1}</span>
            </div>
            <div className="flex gap-2 mt-4 items-center flex-wrap">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => { setEditing(s); setForm({ name: s.name, color: s.color, kind: s.kind, active: s.active }); setOpen(true); }} data-testid={`edit-status-${s.name}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`status-up-${s.name}`} title="Mover para cima"><ArrowUp className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => move(i, 1)} disabled={i === rows.length - 1} data-testid={`status-down-${s.name}`} title="Mover para baixo"><ArrowDown className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className={`h-8 w-8 ${s.active ? "text-amber-600" : "text-emerald-600"}`} onClick={() => toggle(s)} data-testid={`status-active-${s.name}`} title={s.active ? "Inativar" : "Reativar"}><Power className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => del(s)} disabled={s.in_use > 0} title={s.in_use ? "Em uso — inative em vez de excluir" : "Excluir"} data-testid={`delete-status-${s.name}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white dark:bg-slate-900">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Tag className="w-4 h-4" /> {editing ? "Editar status" : "Novo status"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nome *</Label><Input data-testid="status-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex.: Aguardando aprovação" /></div>
            <div><Label>Comportamento</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="status-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-900">{KINDS.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">{KINDS.find((k) => k.id === form.kind)?.help}</p>
            </div>
            <div><Label>Cor</Label>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                {PALETTE.map((c) => <button key={c} onClick={() => setForm({ ...form, color: c })} className={`w-7 h-7 rounded-full border-2 ${form.color === c ? "border-slate-900 dark:border-white scale-110" : "border-transparent"} transition-transform`} style={{ background: c }} data-testid={`status-color-${c.slice(1)}`} />)}
                <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-7 h-7 rounded cursor-pointer" data-testid="status-color-input" />
              </div>
              <Badge variant="outline" className="mt-3 font-semibold" style={statusStyle(form)}>{form.name || "Pré-visualização"}</Badge>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3 border-slate-200 dark:border-slate-800"><Label>Status ativo</Label><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="status-form-active" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-status-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
